import { useState, type ReactNode } from 'react';
import { ShieldAlert, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import { acceptAgeCheck, hasAcceptedAgeCheck } from '@/lib/age-check';
import { strings } from '@/lib/strings';

type AgeGateProps = {
  children: ReactNode;
};

export function AgeGate({ children }: AgeGateProps) {
  const [accepted, setAccepted] = useState(() => hasAcceptedAgeCheck());

  if (accepted) {
    return <>{children}</>;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-6 md:px-6 md:py-10">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(245,158,11,0.18),_transparent_34%),radial-gradient(circle_at_80%_0%,_rgba(239,68,68,0.12),_transparent_26%),linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,1))] dark:bg-[radial-gradient(circle_at_top,_rgba(245,158,11,0.18),_transparent_34%),radial-gradient(circle_at_80%_0%,_rgba(239,68,68,0.12),_transparent_26%),linear-gradient(180deg,rgba(2,6,23,0.98),rgba(2,6,23,1))]" />

      <div className="relative z-10 grid w-full max-w-5xl gap-5 xl:grid-cols-[1.05fr,0.95fr]">
        <section className="surface-panel overflow-hidden p-4 md:p-10">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--surface-soft))] px-3 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" />
              {strings.ageCheck.badge}
            </div>
            <h1 className="mt-4 text-[2.2rem] font-semibold leading-[0.96] tracking-tight text-foreground max-[380px]:text-[1.95rem] max-[380px]:leading-[0.92] md:text-5xl">
              {strings.ageCheck.title}
            </h1>
            <p className="mt-3 max-w-lg text-[15px] leading-7 text-muted-foreground max-[380px]:text-[14px] max-[380px]:leading-6 md:text-lg">
              {strings.ageCheck.subtitle}
            </p>
          </div>
        </section>

        <section className="surface-panel p-5 md:p-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
            <ShieldAlert className="h-5 w-5" />
          </div>

          <h2 className="mt-4 text-[1.85rem] font-semibold leading-tight text-foreground max-[380px]:text-[1.65rem]">{strings.ageCheck.confirmTitle}</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground max-[380px]:leading-5 md:leading-7">{strings.ageCheck.confirmBody}</p>

          <div className="mt-5 rounded-[1.6rem] bg-[hsl(var(--surface-soft))] px-5 py-4.5">
            <p className="text-sm font-semibold text-foreground">{strings.ageCheck.requirementsTitle}</p>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-muted-foreground md:leading-7">
              {strings.ageCheck.requirements.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/70" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            className="primary-pill-button mt-6 w-full"
            onClick={() => {
              acceptAgeCheck();
              setAccepted(true);
            }}
          >
            {strings.ageCheck.confirmCta}
          </button>

          <p className="mt-4 text-center text-xs leading-6 text-muted-foreground">
            {strings.ageCheck.footerPrefix}{' '}
            <Link to="/terms" className="font-semibold text-foreground underline-offset-4 transition hover:underline">
              {strings.terms.linkLabel}
            </Link>
            . {strings.ageCheck.footer}
          </p>
        </section>
      </div>
    </div>
  );
}
